import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import _lodash from 'lodash';

import useSelectedInstitution from 'utils/hooks/institutions/useSelectedInstitution';

import {
  closeObligationsResettingModal,
  openObligationsResettingModal,
  setChosenUserToSet,
  setInstitutionObligations,
  setUserObligations,
  toggleUserFormModal,
} from 'store/entities/users';

import {
  getInstitutionObligations,
  getInstitutionUsersList,
  saveReporter,
  setReporterToObligations,
  updateReporter,
} from 'store/entities/users/thunks';

import { getUserFormFieldProps, userFormFields } from './formConfig';

import InputControl from 'components/FormControls/InputControl';
import ValidationError from 'components/FormControls/ValidationError';
import Modal, { ModalCard } from 'components/UIComponents/Modal';
import { Column, Row, Section } from 'components/UIComponents/Layout';
import { Control, Select, Label, Field } from 'components/UIComponents/Form';
import Button, { IconButton } from 'components/UIComponents/Button';
import { mdiCloseThick } from '@mdi/js';
import { notifySuccess } from 'utils/notifications';
import { getUserDetails } from 'store/entities/auth/thunks';
import { getRemovedObligations } from 'utils/usersUtils';
import { ID_CODE_PREFIX } from 'utils/constants/formConstants';

const UserFormModal = props => {
  const dispatch = useDispatch();
  const { institutionId } = useSelectedInstitution();
  const { t } = useTranslation();
  const {
    users,
    saveReporterSubmitting,
    updateReporterSubmitting,
    userFormModalState: { action, userData },
    userFormModalOpen,
    institutionObligations,
    institutionObligationsLoading,
    userObligations,
    resetObligationsModalOpen,
    obligationsToReset,
    setReporterToObligationsSubmitting,
    chosenUserToSet,
  } = useSelector(state => state.users);

  const { userDetails } = useSelector(state => state.auth);

  const [formDataToEdit, setFormDataToEdit] = useState(null);

  const { handleSubmit, control, errors, setValue } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const omittable = userData ? ['idCode'] : ['username'];
  const formFields = Object.keys(_lodash.omit(userFormFields, omittable));

  useEffect(() => {
    userFormModalOpen
      ? dispatch(getInstitutionObligations(institutionId))
      : dispatch(setInstitutionObligations([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFormModalOpen]);

  useEffect(() => {
    if (userFormModalOpen && action === 'EDIT') {
      formFields.map(field => setValue(field, userData.publicUserDto[field]));
      setValue('notificationEmail', userData.notificationEmail);
      dispatch(setUserObligations(userData.reporterObligations));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, userFormModalOpen, setValue, userData]);

  const formSubmitting = saveReporterSubmitting || updateReporterSubmitting;

  const submitUser = async formData => {
    if (action === 'ADD') {
      const addResult = await submitUserRequest('ADD', formData);

      if (saveReporter.fulfilled.match(addResult)) {
        notifySuccess(t('datagate.external.users.message.saveReporter'));
        dispatch(toggleUserFormModal({}));
        dispatch(setUserObligations([]));
        dispatch(getInstitutionUsersList(institutionId));
      }
    } else {
      const removedObligations = getRemovedObligations(
        userObligations,
        userData.reporterObligations
      );
      if (removedObligations.length === 0) {
        await manageUserEditing(formData);
      } else {
        setFormDataToEdit(formData);
        dispatch(openObligationsResettingModal(removedObligations));
      }
    }
  };

  const submitUserRequest = async (requestAction, formData) => {
    const data = getRequestBody(formData, requestAction);
    const result =
      requestAction === 'ADD'
        ? await dispatch(
            saveReporter({
              institutionId,
              data,
            })
          )
        : await dispatch(
            updateReporter({
              institutionId,
              data,
            })
          );
    return result;
  };

  const getRequestBody = (formData, requestAction) => {
    const id = userData ? userData.publicUserDto.id : null;
    const { idCode, firstName, lastName, notificationEmail } = formData;

    return {
      institutionId,
      publicUserDto: {
        id,
        username:
          requestAction === 'ADD'
            ? ID_CODE_PREFIX + idCode
            : userData?.publicUserDto.username,
        firstName,
        lastName,
        publicUserAuthType:
          requestAction === 'ADD'
            ? 'ID_CODE'
            : userData?.publicUserDto.publicUserAuthType,
      },
      notificationEmail,
      reporterObligations: userObligations,
    };
  };

  const addUserObligation = oblgiation => {
    if (checkUserObligationIsNotDuplicated(oblgiation, userObligations)) {
      dispatch(setUserObligations([...userObligations, oblgiation]));
    }
  };

  const removeUserObligation = obligationId => {
    dispatch(
      setUserObligations([
        ...userObligations.filter(obligation => obligation.id !== obligationId),
      ])
    );
  };

  const checkUserObligationIsNotDuplicated = (userObligation, obligationList) =>
    !obligationList.some(obligation => obligation.id === userObligation.id);

  const manageUserEditing = async formData => {
    const editUserResult = await submitUserRequest('EDIT', formData);
    if (updateReporter.fulfilled.match(editUserResult)) {
      setFormDataToEdit(null);
      dispatch(closeObligationsResettingModal());
      dispatch(toggleUserFormModal({}));
      dispatch(getInstitutionUsersList(institutionId));
      notifySuccess(t('datagate.external.users.message.updateReporter'));
      userData?.publicUserDto.id === userDetails.userId &&
        dispatch(getUserDetails());
    }
  };

  const zeroizeState = () => {
    dispatch(setUserObligations([]));
    dispatch(toggleUserFormModal({}));
  };

  const resetObligationsToAnotherReporter = async () => {
    const resettingResult = await dispatch(
      setReporterToObligations({
        institutionId,
        reporterId: chosenUserToSet.id,
        obligations: obligationsToReset,
      })
    );
    if (setReporterToObligations.fulfilled.match(resettingResult)) {
      await manageUserEditing(formDataToEdit);
    }
  };

  const showFormField = fieldName =>
    !(
      (fieldName !== 'notificationEmail' &&
        action === 'EDIT' &&
        !userData.publicUserDto[fieldName]) ||
      (fieldName === 'notificationEmail' &&
        action === 'EDIT' &&
        !userData[fieldName])
    );

  return (
    <Modal
      show={userFormModalOpen}
      onClose={() => zeroizeState()}
      closeOnBlur={!formSubmitting}
      {...props}
    >
      <ModalCard>
        <form onSubmit={handleSubmit(submitUser)}>
          <ModalCard.Head>
            <ModalCard.Title>
              {t(
                `datagate.external.users.modal.${
                  action === 'EDIT' ? 'editUser' : 'addUser'
                }.title`
              )}
            </ModalCard.Title>
          </ModalCard.Head>
          <ModalCard.Body>
            <Section className="vertical-padding-md">
              {_lodash.chunk(formFields, 2).map((row, i) => (
                <Row key={'userFormRow' + i}>
                  {row.map(fieldName => {
                    const fieldProps = getUserFormFieldProps(
                      fieldName,
                      control,
                      errors
                    );
                    return (
                      showFormField(fieldName) && (
                        <Column
                          key={'userFormField_' + fieldName}
                          className="vertical-padding-none"
                        >
                          <Field>
                            <Control>
                              <Label required={fieldProps?.rules?.required}>
                                {t(fieldProps.label)}
                              </Label>
                              <div
                                className={
                                  fieldName === 'idCode'
                                    ? 'input-group-prefix'
                                    : ''
                                }
                              >
                                {fieldName === 'idCode' && (
                                  <span className="input-group-addon">
                                    {ID_CODE_PREFIX}
                                  </span>
                                )}
                                <InputControl
                                  {...fieldProps}
                                  disabled={
                                    action === 'EDIT' &&
                                    fieldName !== 'notificationEmail'
                                  }
                                />
                              </div>
                            </Control>
                            <ValidationError name={fieldName} errors={errors} />
                          </Field>
                        </Column>
                      )
                    );
                  })}
                </Row>
              ))}
              <Field>
                <Label>
                  {t('datagate.external.users.modal.obligationsSelect')}
                </Label>
                <Select
                  placeholder={t(
                    'datagate.external.users.modal.obligationsSelect.placeholder'
                  )}
                  options={institutionObligations}
                  getOptionLabel={option =>
                    `#${option['id']} ${option['obligationTableName']}`
                  }
                  getOptionValue={option => option}
                  isLoading={institutionObligationsLoading}
                  onChange={option => addUserObligation(option)}
                />
              </Field>
              <>
                {userObligations.map(obligation => (
                  <Row key={obligation.id}>
                    <li>{`#${obligation.id} ${obligation.obligationTableName}`}</li>
                    <IconButton
                      path={mdiCloseThick}
                      size={0.9}
                      onClick={() => removeUserObligation(obligation.id)}
                    />
                  </Row>
                ))}
              </>
            </Section>
          </ModalCard.Body>
          <ModalCard.Foot className="margin-bottom-extra">
            <Button
              type="button"
              color="primary"
              onClick={() => zeroizeState()}
              outlined
              id="abandonUserAdditionButton"
              disabled={formSubmitting}
            >
              &#215; {t('datagate.external.global.cancel')}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={formSubmitting}
              id="confirmUserAdditionButton"
            >
              {t('datagate.external.global.confirm')}
            </Button>
          </ModalCard.Foot>
        </form>
      </ModalCard>

      <Modal
        show={resetObligationsModalOpen}
        onClose={() => {
          setFormDataToEdit(null);
          dispatch(closeObligationsResettingModal());
        }}
      >
        <ModalCard>
          <ModalCard.Head>
            <ModalCard.Title>
              {t('datagate.external.users.modal.obligationsResetting.heading')}
            </ModalCard.Title>
          </ModalCard.Head>
          <ModalCard.Body>
            {userData && (
              <Section className="vertical-padding-md pt-5 pb-5">
                <p>
                  {t(
                    'datagate.external.users.modal.obligationsResetting.select.infoText'
                  )}
                </p>
                {obligationsToReset.map(obligation => (
                  <li
                    key={obligation.id}
                  >{`#${obligation.id} ${obligation.obligationTableName}`}</li>
                ))}
                <Select
                  placeholder={t(
                    'datagate.external.users.modal.obligationsResetting.select.placeholder'
                  )}
                  disabled={setReporterToObligationsSubmitting}
                  options={users.filter(
                    user => user.publicUserDto.id !== userData.publicUserDto.id
                  )}
                  getOptionLabel={option =>
                    `${option.publicUserDto.firstName} ${option.publicUserDto.lastName}`
                  }
                  getOptionValue={option => option}
                  onChange={value =>
                    dispatch(setChosenUserToSet(value.publicUserDto))
                  }
                />
              </Section>
            )}
          </ModalCard.Body>
          <ModalCard.Foot className="margin-bottom-extra">
            <Button
              type="button"
              color="primary"
              onClick={() => {
                setFormDataToEdit(null);
                dispatch(closeObligationsResettingModal());
              }}
              outlined
            >
              &#215; {t('datagate.external.global.cancel')}
            </Button>
            <Button
              type="button"
              color="primary"
              disabled={
                formSubmitting ||
                setReporterToObligationsSubmitting ||
                chosenUserToSet === null
              }
              id="confirmUserAdditionButton"
              onClick={() => resetObligationsToAnotherReporter()}
            >
              {t('datagate.external.global.confirm')}
            </Button>
          </ModalCard.Foot>
        </ModalCard>
      </Modal>
    </Modal>
  );
};

export default UserFormModal;
